import { Module } from '@/modules/companies'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback, useEffect, useMemo } from 'react'
import { HEARTBEAT_INTERVAL_MS, STORYLANE_SCRIPT_SRC } from './consts'
import {
  REGISTER_LEARNING_DEMO_HEARTBEAT_MUTATION,
  REGISTER_LEARNING_DEMO_VIEW_MUTATION,
} from './mutations'

/**
 * Storylane's loader script drives the embedded demos. It is added on demand by
 * the learning page rather than in the app shell, so the request is only made
 * for users who actually open the page.
 */
export function useStorylaneScript() {
  useEffect(() => {
    if (document.querySelector(`script[src="${STORYLANE_SCRIPT_SRC}"]`)) return

    const script = document.createElement('script')
    script.src = STORYLANE_SCRIPT_SRC
    script.async = true
    script.setAttribute('data-verify-origin', '')
    document.body.appendChild(script)
  }, [])
}

export function useModuleFolderLabels() {
  const translations = useTranslate({
    general: 'learning.general',
    module: {
      activities: 'common.activities',
      absences: 'common.absences',
      companySettings: 'settings.company-settings',
      dashboard: 'common.dashboard',
      driverApp: 'common.driver-app',
      formCreator: 'common.formcreator',
      fleet: 'vehicles.fleet',
      forum: 'common.forum',
      handbook: 'common.handbook',
      infringements: 'common.infringements',
      issues: 'common.issues',
      learning: 'learning.title',
      planner: 'common.planner',
      routePlanner: 'common.route-planner',
      salary: 'common.salary',
      terminal: 'common.terminal',
      timesheets: 'common.timesheets',
      users: 'common.users',
      vehicles: 'common.vehicles',
      workInfringements: 'infringements.work-day-infringements',
      tachographInfringements: 'infringements.tachograph-infringements',
    },
  })

  const labels = useMemo<Record<string, string>>(
    () => ({
      [Module.ACTIVITIES]: translations.module.activities,
      [Module.ABSENCES]: translations.module.absences,
      [Module.COMPANY_SETTINGS]: translations.module.companySettings,
      [Module.DASHBOARD]: translations.module.dashboard,
      [Module.DRIVER_APP]: translations.module.driverApp,
      [Module.FORMCREATOR]: translations.module.formCreator,
      [Module.FLEET]: translations.module.fleet,
      [Module.FORUM]: translations.module.forum,
      [Module.HANDBOOK]: translations.module.handbook,
      [Module.INFRINGEMENTS]: translations.module.infringements,
      [Module.ISSUES]: translations.module.issues,
      [Module.LEARNING]: translations.module.learning,
      [Module.PLANNER]: translations.module.planner,
      [Module.ROUTEPLANNER]: translations.module.routePlanner,
      [Module.SALARY]: translations.module.salary,
      [Module.TERMINAL]: translations.module.terminal,
      [Module.TIMESHEETS]: translations.module.timesheets,
      [Module.USERS]: translations.module.users,
      [Module.VEHICLES]: translations.module.vehicles,
      [Module.WORK_DAY_INFRINGEMENTS]: translations.module.workInfringements,
      [Module.TACHOGRAPH_INFRINGEMENTS]:
        translations.module.tachographInfringements,
    }),
    [translations.module]
  )

  return useCallback(
    (module: string | null) =>
      !module ? translations.general : labels[module] ?? module,
    [labels, translations.general]
  )
}

/**
 * Records that the user opened a demo, then reports the time it stayed open.
 *
 * The demo runs in a cross-origin iframe, so its internal state is not
 * readable — what we can observe is that it was opened and for how long, and
 * only while the tab is actually visible.
 */
export function useDemoViewTracking(demo: string, open: boolean) {
  const [registerView] = useMutation(REGISTER_LEARNING_DEMO_VIEW_MUTATION)
  const [registerHeartbeat] = useMutation(
    REGISTER_LEARNING_DEMO_HEARTBEAT_MUTATION
  )

  useEffect(() => {
    if (!open) return

    void registerView({ variables: { demo } })
  }, [demo, open, registerView])

  useEffect(() => {
    if (!open) return

    let visibleSince: number | null =
      document.visibilityState === 'visible' ? Date.now() : null
    let pendingSeconds = 0

    function collect() {
      if (visibleSince === null) return

      pendingSeconds += Math.round((Date.now() - visibleSince) / 1000)
      visibleSince = null
    }

    function flush() {
      collect()
      if (pendingSeconds < 1) return

      const seconds = pendingSeconds
      pendingSeconds = 0
      void registerHeartbeat({ variables: { demo, seconds } })
    }

    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return flush()

      visibleSince = Date.now()
    }

    const interval = setInterval(() => {
      flush()
      if (document.visibilityState === 'visible') visibleSince = Date.now()
    }, HEARTBEAT_INTERVAL_MS)

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      flush()
    }
  }, [demo, open, registerHeartbeat])
}
